import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs, doc, updateDoc } from "firebase/firestore";
import { txtdb, auth } from "../../firebase-config";
import { signOut } from "firebase/auth";
import { NavLink, useNavigate } from "react-router-dom";
import { PiUserFocusFill, PiCheckCircleFill, PiXCircleFill, PiCheckSquareDuotone } from "react-icons/pi";
import { ImSpinner8 } from "react-icons/im";
import { IoIosLogOut } from "react-icons/io";
import { AiOutlineMenu } from "react-icons/ai";
import logo from "../../stock/nexus/logo.png"; // Make sure this path is correct for your setup

function AdminKycReview() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // Controls the mobile/admin nav menu
  
  // KYC States
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); 

  useEffect(() => {
    fetchPendingKyc();
  }, []);

  const fetchPendingKyc = async () => {
    try {
      setLoading(true);
      const q = query(collection(txtdb, "kycInfo"), where("status", "==", "pending"));
      const querySnapshot = await getDocs(q);
      const users = [];
      querySnapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() });
      });
      setPendingUsers(users);
    } catch (error) {
      console.error("Error fetching pending KYC:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (uid, newStatus) => {
    setActionLoading(uid);
    try {
      const userRef = doc(txtdb, "kycInfo", uid);
      await updateDoc(userRef, {
        status: newStatus
      });
      // Remove the user from the UI list after successful update
      setPendingUsers(pendingUsers.filter(user => user.id !== uid));
    } catch (error) {
      console.error(`Error updating status to ${newStatus}:`, error);
      alert("Failed to update status.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => navigate("/login"))
      .catch((error) => console.error("Logout error:", error));
  };

  return (
    <div className='layout admin'>
      <div className="content">
        
        {/* --- Top Navigation Header (Matching your CopyTrades style) --- */}
        <div className="transactions-container container">
          <div className="navigation-container">
            <NavLink className="mobile">
              <img src={logo} alt="nexusforgefx" />
              <p className='name'>KYC Reviews</p>
            </NavLink>
            <p className='desktop'>KYC Verification Queue</p>
            
            <div className="actions desktop">
              <div className="quick" onClick={() => setOpen(!open)}>
                Admin Pages <AiOutlineMenu />
              </div>
              <div className="notif" onClick={handleLogout}>
                <IoIosLogOut className="icon" title="logout" />
              </div>
            </div>

            <div className="actions mobile">
              <div className="notif" onClick={handleLogout}>
                <IoIosLogOut className="icon" title="logout" />
              </div>
              <div className="nav" onClick={() => setOpen(!open)}>
                <AiOutlineMenu className='icon' />
              </div>
            </div>
          </div>

          {/* Admin Menu Dropdown */}
          {open && (
            <div className="admin-nav">
                     <NavLink to="/pendingDeposits">Pending Deposits</NavLink>
                     <NavLink to="/all-users">All Users</NavLink>
                     <NavLink to="/copytrades">Active Copy Trades</NavLink>
                     <NavLink to="/adminKycReview">KYC Reviews</NavLink>
                     <NavLink to="/admin-wallets">Wallet Management</NavLink>
                   </div>
          )}
        </div>

        {/* --- Main KYC Content Area --- */}
        <div className="pages-content" onClick={() => setOpen(false)}>
          <div className="admin-review-page">
            
            <div className="header-bar">
              <h1><PiUserFocusFill /> Verification Queue</h1>
              <p>{pendingUsers.length} Pending Applications</p>
            </div>

     {loading ? (
              <div className="loader"><ImSpinner8 className="spin" /></div>
            ) : pendingUsers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon-wrapper">
                  <PiCheckSquareDuotone className="empty-icon" />
                </div>
                <h2>All caught up!</h2>
                <p>You have reviewed all pending applications. Time for a coffee break.</p>
              </div>
            ) : (
              <div className="users-grid">
                {pendingUsers.map((user) => (
                  <div key={user.id} className="user-card">
                    <div className="card-header">
                      <h3>{user.firstName} {user.lastName}</h3>
                      <span className="date">{new Date(user.createdAt).toLocaleDateString()}</span>
                    </div>
                    
                    <div className="details">
                      <p><strong>Email:</strong> {user.email}</p>
                      <p><strong>DOB:</strong> {user.dob} ({user.gender})</p>
                      <p><strong>Country:</strong> {user.country}</p>
                      <p><strong>Phone:</strong> {user.dialCode} {user.phone}</p>
                      <div className="id-box">
                        <p><strong>ID Type:</strong> {user.idType}</p>
                        <p><strong>ID Number:</strong> {user.idNumber}</p>
                      </div>
                    </div>

                    <div className="actions">
                      <button 
                        className="reject-btn"
                        onClick={() => handleAction(user.id, "rejected")}
                        disabled={actionLoading === user.id}
                      >
                        {actionLoading === user.id ? <ImSpinner8 className="spin" /> : <><PiXCircleFill /> Reject</>}
                      </button>
                      <button 
                        className="approve-btn"
                        onClick={() => handleAction(user.id, "approved")}
                        disabled={actionLoading === user.id}
                      >
                        {actionLoading === user.id ? <ImSpinner8 className="spin" /> : <><PiCheckCircleFill /> Approve</>}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
          </div>
        </div>
        
      </div>
    </div>
  );
}

export default AdminKycReview;